import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";

import QuickCaptureBtn from "./QuickCaptureBtn";
import { burstPieces, pickEmoji, pickHeadline } from "./FrenchNounArticles/celebration";
import SayIt from "./TravelPhrases/SayIt";
import Wallpaper from "./TravelPhrases/Wallpaper";
import useTheme, { THEME_LABELS, THEMES } from "./Labs/useTheme";
import {
  REVIEW_PACK, buildSteps, completedStops, grade, isDue, isLearned, mergeCards, nextStop,
  STORAGE_PREFIX, pickChoices, readStored, sectionsOf, stopProgress, writeStored
} from "./TravelPhrases/srs";
import { stopIcon } from "./TravelPhrases/stops";
import { speak, useBrowserVoices } from "./TravelPhrases/speech";

// Practice for one Wikivoyage phrasebook: the phrasebook's sections as stops
// on a route, short multiple-choice rounds at each, a passport stamp for each
// stop finished, and spaced repetition in localStorage so it works without an
// account (and syncs to one, for people who choose to sign in). The full phrase list is rendered server-side under this component
// (for search engines and for reading); its play buttons are wired up here.

function playHistoryEntry() {
  return !!(window.history.state && window.history.state.tpPlay);
}

function enterPlayHistory() {
  if (playHistoryEntry()) return;

  window.history.pushState({ tpPlay: true }, "", `${window.location.pathname}${window.location.search}#play`);
}

function audioUrl(audioBaseUrl, phrase) {
  if (!audioBaseUrl || !phrase || !phrase.audio) return null;

  return `${audioBaseUrl}/${phrase.audio}.mp3`;
}

function Pronunciation({ phrase }) {
  if (!phrase.romanization && !phrase.pron) return null;

  return (
    <div className="tp-pron">
      {phrase.romanization && <span className="tp-roman">{phrase.romanization}</span>}
      {phrase.romanization && phrase.pron && " · "}
      {phrase.pron && <span>{phrase.pron}</span>}
    </div>
  );
}

Pronunciation.propTypes = {
  phrase: PropTypes.object.isRequired,
};

// Clozemaster's segmented progress bar (the dashboard's pixel-progress-bar):
// filled blocks out of a fixed number, rather than a smooth fill.
function PixelBar({ segments, total, value }) {
  const filled = total ? Math.round((value / total) * segments) : 0;
  // Never round a little progress down to nothing, or all of it up to done.
  const shown = value > 0 && filled === 0 ? 1 : (value < total && filled === segments ? segments - 1 : filled);

  return (
    <span aria-hidden="true" className="tp-pixelbar">
      {Array.from({ length: segments }, (_, i) => (
        <span className={i < shown ? "tp-pixel tp-pixel-on" : "tp-pixel"} key={i} />
      ))}
    </span>
  );
}

PixelBar.propTypes = {
  segments: PropTypes.number.isRequired,
  total: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

function PlayButton({ available, onPlay, phrase }) {
  if (!available) return null;

  return (
    <button
      aria-label={`Play ${phrase.target}`}
      className="tp-play"
      onClick={(event) => {
        event.stopPropagation();
        onPlay(phrase);
      }}
      type="button"
    >
      <span className="glyphicon glyphicon-volume-up" />
    </button>
  );
}

PlayButton.propTypes = {
  available: PropTypes.bool,
  onPlay: PropTypes.func.isRequired,
  phrase: PropTypes.object.isRequired,
};

function ReportPhrase({ phrase, reportUrl }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [state, setState] = useState("idle");

  if (state === "sent") return <div className="tp-report tp-report-sent">Thanks — we&apos;ll take a look.</div>;

  if (!open) {
    return (
      <button className="tp-report-open" onClick={() => setOpen(true)} type="button">
        Report a problem
      </button>
    );
  }

  const submit = (event) => {
    event.preventDefault();
    if (state === "sending") return;

    setState("sending");
    const token = document.querySelector("meta[name=\"csrf-token\"]");

    fetch(reportUrl, {
      body: JSON.stringify({ reason, word: `${phrase.en} = ${phrase.target}` }),
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": token ? token.getAttribute("content") : "",
      },
      method: "POST",
    })
      .then((response) => setState(response.ok ? "sent" : "error"))
      .catch(() => setState("error"));
  };

  return (
    <form className="tp-report" onSubmit={submit}>
      <textarea
        aria-label="What's wrong with this phrase?"
        autoFocus
        onChange={(event) => setReason(event.target.value)}
        placeholder="Wrong translation, typo, misleading pronunciation, bad audio…"
        rows={2}
        value={reason}
      />
      <div className="tp-report-actions">
        <button className="btn btn-success btn-xs" disabled={!reason.trim() || state === "sending"} type="submit">
          {state === "sending" ? "Sending…" : "Send"}
        </button>
        <button className="btn btn-link btn-xs" onClick={() => setOpen(false)} type="button">Cancel</button>
        {state === "error" && <span className="tp-report-error">Couldn&apos;t send — try again?</span>}
      </div>
    </form>
  );
}

ReportPhrase.propTypes = {
  phrase: PropTypes.object.isRequired,
  reportUrl: PropTypes.string.isRequired,
};

function CaptureButton({ phrase, quickCapture, small }) {
  if (!quickCapture) return null;

  return (
    <QuickCaptureBtn
      autoSubmitInitialText
      className={small ? "tp-capture tp-capture-small" : "btn btn-default tp-capture"}
      collectionsUrl={quickCapture.collectionsUrl}
      demoEntries={quickCapture.demoEntries}
      entriesUrl={quickCapture.entriesUrl}
      importsUrl={quickCapture.importsUrl}
      initialText={phrase.target}
      isPro={quickCapture.isPro}
      key={phrase.id}
      targetLanguageEnglishName={quickCapture.targetLanguageEnglishName}
      targetLanguageIso={quickCapture.targetLanguageIso}
      targetLanguageName={quickCapture.targetLanguageName}
      // Low-key on purpose: a plain "Save" reads as a tool, not an ad, on a
      // page that gets shared around. The modal explains Clozemaster.
      title="Save to Clozemaster to keep reviewing it in context"
    >
      <span aria-hidden="true" className="glyphicon glyphicon-bookmark" />
      {small ? <span className="sr-only">Save</span> : " Save"}
    </QuickCaptureBtn>
  );
}

CaptureButton.propTypes = {
  phrase: PropTypes.object.isRequired,
  quickCapture: PropTypes.object,
  small: PropTypes.bool,
};

export default function TravelPhrases({
  audioBaseUrl,
  correctAudioUrl,
  gameSettingsUrl,
  isSignedIn,
  languageName,
  phrases,
  quickCapture,
  reportUrl,
  shareUrl,
  signInUrl,
  signUpUrl,
  slug,
  speechLang,
  stateUrl,
  takeaways,
}) {
  const [cards, setCards] = useState(() => readStored(`${slug}:cards`, {}));
  const [said, setSaid] = useState(() => readStored(`${slug}:said`, {}));
  const [settings, setSettings] = useState(() => readStored("settings", { autoplay: true, sounds: true }));
  const [selected, setSelected] = useState(() => nextStop(phrases, readStored(`${slug}:cards`, {})));
  const [pack, setPack] = useState(null);
  const [phase, setPhase] = useState("path");
  const [queue, setQueue] = useState([]);
  const [position, setPosition] = useState(0);
  const [choices, setChoices] = useState([]);
  const [picked, setPicked] = useState(null);
  const [results, setResults] = useState([]);
  const [celebration, setCelebration] = useState(null);
  const [stampedBefore, setStampedBefore] = useState([]);
  const [shareState, setShareState] = useState(null);
  const [brokenAudio, setBrokenAudio] = useState(false);
  const [roundsPlayed, setRoundsPlayed] = useState(0);
  const audioRef = useRef(null);
  const cardsRef = useRef(cards);
  const { chooseTheme, systemDark, theme } = useTheme({ gameSettingsUrl, isSignedIn, prefix: STORAGE_PREFIX });

  const sections = useMemo(() => sectionsOf(phrases), [phrases]);
  const learnedCount = phrases.filter((phrase) => isLearned(cards[phrase.id])).length;
  const dueCount = phrases.filter((phrase) => isDue(cards[phrase.id], Date.now())).length;
  const stamped = completedStops(phrases, cards);
  const upNext = nextStop(phrases, cards);
  const tripReady = stamped.length === sections.length;
  // Which voice plays a phrase: "auto" (the generated audio, else the best
  // device voice), "file" (the generated audio), or a device voice by name.
  const [voiceChoice, setVoiceChoice] = useState(() => readStored(`${slug}:voice`, "auto"));
  const deviceVoices = useBrowserVoices(speechLang);
  const browserVoice = deviceVoices.find((voice) => voice.name === voiceChoice) || deviceVoices[0] || null;
  const fileAudio = !!audioBaseUrl && !brokenAudio && (voiceChoice === "auto" || voiceChoice === "file");
  const hasAudio = fileAudio || !!browserVoice;
  const canPlay = (phrase) => hasAudio && !!(browserVoice || phrase.audio);
  const [showVoices, setShowVoices] = useState(false);

  const chooseVoice = (choice) => {
    setVoiceChoice(choice);
    writeStored(`${slug}:voice`, choice);
  };

  const storeCards = useCallback((next) => {
    cardsRef.current = next;
    writeStored(`${slug}:cards`, next);
    setCards(next);
  }, [slug]);

  // Signed in, progress also lives on the account. The two copies are merged
  // card by card on load, and each finished round is sent up.
  const pushCards = useCallback((changed) => {
    if (!stateUrl || !Object.keys(changed).length) return;

    const token = document.querySelector("meta[name=\"csrf-token\"]");
    fetch(stateUrl, {
      body: JSON.stringify({ cards: changed }),
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": token ? token.getAttribute("content") : "",
      },
      method: "PATCH",
    }).catch(() => {});
  }, [stateUrl]);

  useEffect(() => {
    if (!stateUrl) return;

    fetch(stateUrl, { credentials: "same-origin", headers: { Accept: "application/json" } })
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => {
        if (!data) return;

        const merged = mergeCards(cardsRef.current, data.cards);
        storeCards(merged);
        setSelected((current) => current || nextStop(phrases, merged));
        // Whatever this browser knew that the account didn't.
        const missing = {};
        Object.entries(merged).forEach(([id, card]) => {
          const theirs = (data.cards || {})[id];
          if (!theirs || (theirs.at || 0) < (card.at || 0)) missing[id] = card;
        });
        pushCards(missing);
      })
      .catch(() => {});
  }, [phrases, pushCards, stateUrl, storeCards]);

  const updateSettings = (change) => {
    setSettings((previous) => {
      const next = { ...previous, ...change };
      writeStored("settings", next);
      return next;
    });
  };

  // Resolves when the phrase has finished playing, so the recorder's "play
  // both" can follow it with the learner's own take.
  const play = useCallback((phrase) => {
    if (!phrase) return Promise.resolve();

    const sayIt = () => speak(phrase.speak || phrase.target, browserVoice);
    const url = fileAudio ? audioUrl(audioBaseUrl, phrase) : null;
    if (!url) return sayIt();

    if (audioRef.current) audioRef.current.pause();
    return new Promise((resolve) => {
      const audio = new Audio(url);
      audioRef.current = audio;
      audio.onended = () => resolve();
      // A single missing file falls back to the browser voice too.
      audio.onerror = () => sayIt().then(resolve);
      audio.play().catch(() => resolve());
    });
  }, [audioBaseUrl, browserVoice, fileAudio]);

  // Audio is generated after a deck is built, so a fresh deck can be live
  // before its files are. One failed probe switches to the browser voice
  // rather than leaving a page of dead buttons.
  useEffect(() => {
    const first = phrases.find((phrase) => phrase.audio);
    const url = audioUrl(audioBaseUrl, first);
    if (!url) return;

    const probe = new Audio();
    probe.preload = "metadata";
    probe.onerror = () => setBrokenAudio(true);
    probe.src = url;
  }, [audioBaseUrl, phrases]);

  // The server-rendered phrase list below the component.
  useEffect(() => {
    const byAudio = {};
    phrases.forEach((phrase) => { if (phrase.audio) byAudio[phrase.audio] = phrase; });

    document.body.classList.toggle("tp-audio-ready", hasAudio);
    const onClick = (event) => {
      const button = event.target.closest("[data-tp-audio]");
      if (!button) return;

      play(byAudio[button.getAttribute("data-tp-audio")]);
    };

    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, [hasAudio, phrases, play]);

  // "Find a phrase" over the server-rendered list: matching rows stay, groups
  // with a match open, and clearing it puts the list back as it was. Accents
  // are ignored, so "obrigado" finds "obrigado" however it's typed.
  useEffect(() => {
    const input = document.querySelector("[data-tp-search]");
    if (!input) return undefined;

    const fold = (text) => text.normalize("NFD").replace(/\p{M}/gu, "").toLowerCase();
    const groups = [...document.querySelectorAll(".tp-list-group")];
    const initiallyOpen = groups.map((group) => group.open);
    const rows = [...document.querySelectorAll(".tp-row")].map((row) => [row, fold(row.textContent)]);
    const empty = document.querySelector(".tp-list-empty");

    const onInput = () => {
      const query = fold(input.value.trim());
      rows.forEach(([row, text]) => { row.hidden = !!query && !text.includes(query); });
      let anyMatch = false;
      groups.forEach((group, index) => {
        const matches = group.querySelectorAll(".tp-row:not([hidden])").length > 0;
        anyMatch = anyMatch || matches;
        group.hidden = !!query && !matches;
        group.open = query ? matches : initiallyOpen[index];
      });
      if (empty) empty.hidden = !query || anyMatch;
    };

    input.addEventListener("input", onInput);
    return () => input.removeEventListener("input", onInput);
  }, []);

  const current = phase === "question" ? queue[position] : null;
  const kind = current ? current.kind : null;
  const isLearnStep = kind === "learn";

  const startRound = (chosenPack) => {
    const steps = buildSteps(phrases, cards, chosenPack, Date.now(), hasAudio);
    // The review pack empties as you clear it.
    if (!steps.length) {
      setPhase("path");
      return;
    }

    setPack(chosenPack);
    setStampedBefore(completedStops(phrases, cards));
    setQueue(steps.map((step) => ({ ...step, retry: false })));
    setPosition(0);
    setPicked(null);
    setResults([]);
    setShareState(null);
    setPhase("question");
    enterPlayHistory();
    // Started from a stamp further down the page: bring the question up.
    window.requestAnimationFrame(() => {
      const layout = document.querySelector(".tp-layout");
      if (layout && layout.getBoundingClientRect().top < 0) layout.scrollIntoView({ block: "start" });
    });
  };

  // Choices are drawn when a question appears, not on every render.
  useEffect(() => {
    if (!current) return;

    if (current.kind === "learn") {
      setChoices([]);
    } else {
      setChoices(pickChoices(current, phrases, current.kind === "produce" ? "target" : "en"));
    }
    // A listening question is the audio, so it always plays; elsewhere it
    // follows the auto-play setting.
    if (current.kind === "listen" || (settings.autoplay && current.kind !== "produce")) play(current);
    // Only when the question changes - grading it updates cards, which mustn't redraw.
  }, [phase, position, queue]);

  const answer = (choice) => {
    if (picked || !current) return;

    const right = choice.id === current.id;
    setPicked(choice);

    // Hearing the phrase is the better reward, so the chime only plays when
    // the phrase itself isn't about to.
    if (settings.autoplay && kind === "produce" && hasAudio) {
      play(current);
    } else if (right && settings.sounds && correctAudioUrl) {
      new Audio(correctAudioUrl).play().catch(() => {});
    }

    // Only a first attempt is graded; a missed phrase comes back once at the
    // end of the round so the round ends on getting it right.
    if (!current.retry) {
      const graded = grade(cardsRef.current[current.id], right, Date.now());
      storeCards({ ...cardsRef.current, [current.id]: graded });
      setResults((previous) => [...previous, { phrase: current, right }]);
    }
    if (!right && !current.retry) {
      setQueue((previous) => [...previous, { ...current, retry: true }]);
    }
  };

  const next = () => {
    if (!picked && !isLearnStep) return;

    setPicked(null);
    if (position + 1 < queue.length) {
      setPosition(position + 1);
      return;
    }

    const right = results.filter((result) => result.right).length;
    const accuracy = results.length ? Math.round((right / results.length) * 100) : 0;
    setCelebration({
      emoji: pickEmoji(),
      headline: pickHeadline(accuracy, results.length),
      pieces: burstPieces(accuracy),
    });
    pushCards(Object.fromEntries(results.map(({ phrase }) => [phrase.id, cardsRef.current[phrase.id]])));
    setRoundsPlayed((count) => count + 1);
    setSelected(nextStop(phrases, cardsRef.current));
    setPhase("done");
  };

  // Leaving mid-round loses nothing: every answer is graded and saved as it's
  // given. This just sends the round's answers up for a signed in user.
  const showPassport = () => {
    pushCards(Object.fromEntries(results.map(({ phrase }) => [phrase.id, cardsRef.current[phrase.id]])));
    setPicked(null);
    setSelected(nextStop(phrases, cardsRef.current));
    setPhase("path");
  };
  const showPassportRef = useRef(showPassport);
  showPassportRef.current = showPassport;

  // Playing is a history entry (#play), so the browser's back button returns
  // to the passport rather than leaving the page. Our own "back" buttons go
  // through history too, so back and forward stay in step.
  const leaveRound = () => {
    if (playHistoryEntry()) window.history.back();
    else showPassport();
  };

  useEffect(() => {
    // A reload or shared link with #play starts on the passport.
    if (window.location.hash === "#play") {
      window.history.replaceState(null, "", window.location.pathname + window.location.search);
    }

    const onPopState = (event) => {
      if (!(event.state && event.state.tpPlay)) showPassportRef.current();
    };
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  const markSaid = (phrase) => {
    setSaid((previous) => {
      const updated = { ...previous, [phrase.id]: true };
      writeStored(`${slug}:said`, updated);
      return updated;
    });
  };

  useEffect(() => {
    const onKey = (event) => {
      if (event.target.closest("input, textarea, select, button.tp-sayit-start, .modal")) return;

      if (phase === "question" && !picked && !isLearnStep && /^[1-4]$/.test(event.key)) {
        const choice = choices[Number(event.key) - 1];
        if (choice) answer(choice);
      } else if (phase === "question" && (picked || isLearnStep) && event.key === "Enter") {
        event.preventDefault();
        next();
      }
    };

    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  const share = () => {
    const text = tripReady
      ? `I'm trip-ready: I learned all ${phrases.length} ${languageName} travel phrases on Clozemaster 🧳`
      : `I've collected ${stamped.length} of ${sections.length} ${languageName} phrase stamps on Clozemaster 🛂`;

    if (navigator.share) {
      navigator.share({ text, title: `${languageName} phrases for travel`, url: shareUrl }).catch(() => {});
      return;
    }

    const copy = navigator.clipboard ? navigator.clipboard.writeText(`${text} ${shareUrl}`) : Promise.reject();
    copy.then(() => setShareState("copied")).catch(() => setShareState("failed"));
  };

  const shareButton = (
    <div className="tp-share">
      <button className="btn btn-link" onClick={share} type="button">
        <span className="glyphicon glyphicon-share-alt" /> {tripReady ? "Share: I'm trip-ready" : "Share your passport"}
      </button>
      {shareState === "copied" && <span className="tp-share-note">Link copied!</span>}
      {shareState === "failed" && <span className="tp-share-note">Copy this page&apos;s address to share it.</span>}
    </div>
  );

  // Quiet and optional on purpose: everything works without an account.
  const saveNudge = !stateUrl && signUpUrl && roundsPlayed > 0 && (
    <p className="tp-save">
      Your progress is saved in this browser.{" "}
      <a href={signUpUrl}>Create a free account</a> to keep it on all your devices
      {signInUrl && <> (or <a href={signInUrl}>sign in</a>)</>}.
    </p>
  );

  // Device voices vary - a missing language voice is why a phrase can come out
  // in English - so the choice is the learner's, and the current one is named.
  const generatedAvailable = !!audioBaseUrl && !brokenAudio;
  let autoLabel = "no voice";
  if (generatedAvailable) autoLabel = "generated audio";
  else if (deviceVoices[0]) autoLabel = deviceVoices[0].name;
  const voicePicker = (generatedAvailable || deviceVoices.length > 0) ? (
    <label className="tp-voice">
      <span aria-hidden="true">🗣</span>
      <select
        aria-label="Voice"
        className="tp-voice-select"
        onChange={(event) => chooseVoice(event.target.value)}
        value={voiceChoice}
      >
        <option value="auto">Voice: Auto ({autoLabel})</option>
        {generatedAvailable && <option value="file">Generated audio</option>}
        {deviceVoices.map((voice) => (
          <option key={voice.name} value={voice.name}>{voice.name} ({voice.lang})</option>
        ))}
      </select>
    </label>
  ) : (
    speechLang && <span className="tp-voice tp-voice-none">🔇 No {languageName} voice on this device</span>
  );

  const cycleTheme = () => chooseTheme(THEMES[(THEMES.indexOf(theme) + 1) % THEMES.length]);
  // Auto gets its own icon, so the header button says which mode it's in.
  const themeIcon = { auto: "🌓", dark: "🌙", light: "☀️" }[theme];

  // Settings as small toggle pills, next to the take-it-with-you formats - one
  // toolbar rather than checkboxes scattered between sections.
  const toolbar = (
    <div className="tp-toolbar">
      {hasAudio && (
        <button
          aria-pressed={!!settings.autoplay}
          className={`tp-tool${settings.autoplay ? " tp-tool-on" : ""}`}
          onClick={() => updateSettings({ autoplay: !settings.autoplay })}
          type="button"
        >
          🔊 Auto-play {settings.autoplay ? "on" : "off"}
        </button>
      )}
      <button
        aria-pressed={!!settings.sounds}
        className={`tp-tool${settings.sounds ? " tp-tool-on" : ""}`}
        onClick={() => updateSettings({ sounds: !settings.sounds })}
        type="button"
      >
        🔔 Sounds {settings.sounds ? "on" : "off"}
      </button>
      {voicePicker}
      <button
        className="tp-tool tp-tool-on"
        onClick={cycleTheme}
        title={theme === "auto" ? `Following your system (${systemDark ? "dark" : "light"})` : "Change theme"}
        type="button"
      >
        {themeIcon} Theme: {THEME_LABELS[theme]}
      </button>
      {takeaways && (
        <>
          <span aria-hidden="true" className="tp-tool-divider" />
          <a className="tp-tool" href={takeaways.cheatSheetUrl}>🖨 Cheat sheet</a>
          <Wallpaper {...takeaways.wallpaper} />
        </>
      )}
    </div>
  );

  if (phase === "path") {
    const target = selected || upNext;
    const targetProgress = target ? stopProgress(phrases, cards, target) : null;
    let ctaLabel = "Practice again";
    if (targetProgress && !targetProgress.complete) ctaLabel = targetProgress.learned ? "Continue" : "Start";

    return (
      <div className="tp">
        {/* "Up next", pulled up over the bottom of the hero: the first thing a
            visitor from search sees is one stop and one button. The totals
            live on the passport below rather than repeating here. */}
        <div className="tp-startcard">
          <span aria-hidden="true" className="tp-upnext-icon">{target ? stopIcon(target) : "🏁"}</span>
          <div className="tp-upnext-body">
            <div className="tp-upnext-kicker">
              {target ? `Up next · Stop ${sections.indexOf(target) + 1} of ${sections.length}` : "All stamps collected"}
            </div>
            <div className="tp-upnext-name">{target || "Trip-ready!"}</div>
            {targetProgress && (
              <div className="tp-upnext-progress">
                <PixelBar segments={Math.min(targetProgress.total, 10)} total={targetProgress.total} value={targetProgress.learned} />
                <span>{targetProgress.learned}/{targetProgress.total} learned</span>
              </div>
            )}
          </div>
          <div className="tp-upnext-actions">
            {target ? (
              <button className="btn btn-success btn-lg tp-primary" onClick={() => startRound(target)} type="button">
                ▶ {ctaLabel}
              </button>
            ) : (
              <button className="btn btn-success btn-lg tp-primary" onClick={share} type="button">🧳 Share it</button>
            )}
            {dueCount > 0 && (
              <button className="btn btn-link tp-review-btn" onClick={() => startRound(REVIEW_PACK)} type="button">
                🔁 Review {dueCount} due
              </button>
            )}
          </div>
        </div>

        {shareState && (
          <p className="tp-share-note tp-share-note-block">
            {shareState === "copied" ? "Link copied!" : "Copy this page's address to share it."}
          </p>
        )}

        {/* The phrasebook's own sections, in its own order, as spots on a
            passport page. Any one can be picked; filling the page is the goal. */}
        <section aria-label={`${languageName} passport`} className="tp-passport">
          {/* Like the holder's details on a real passport: the language, with
              the phrase count under it. The full-width bar is phrases learned. */}
          <div className="tp-passport-head">
            <span>Passport</span>
            <span className="tp-passport-holder">
              <span className="tp-passport-name">{languageName}</span>
              <span className="tp-passport-phrases">{learnedCount} of {phrases.length} phrases learned</span>
            </span>
            <span>{stamped.length}/{sections.length} stamps</span>
          </div>
          <div className="tp-passport-bar">
            <PixelBar segments={40} total={phrases.length} value={learnedCount} />
          </div>

          <ol className="tp-stamps">
            {sections.map((section, index) => {
              const { complete, learned, total } = stopProgress(phrases, cards, section);
              const classes = [
                "tp-spot",
                complete ? "tp-spot-complete" : (learned ? "tp-spot-started" : "tp-spot-new"),
                section === upNext ? "tp-spot-next" : "",
                section === target ? "tp-spot-selected" : "",
              ].filter(Boolean).join(" ");

              return (
                <li key={section}>
                  <button
                    aria-label={`${index + 1}. ${section}: ${complete ? "stamped" : `${learned} of ${total} learned`}`}
                    aria-pressed={section === target}
                    className={classes}
                    onClick={() => startRound(section)}
                    style={{ "--tilt": `${((index * 37) % 17) - 8}deg` }}
                    type="button"
                  >
                    <span className="tp-spot-num">{index + 1}</span>
                    <span aria-hidden="true" className="tp-spot-ring">{stopIcon(section)}</span>
                    <span className="tp-spot-name">{section}</span>
                    <PixelBar segments={Math.min(total, 8)} total={total} value={learned} />
                    <span className="tp-spot-meta">{complete ? "Stamped" : `${learned}/${total}`}</span>
                  </button>
                </li>
              );
            })}
            <li>
              <div className={`tp-spot tp-spot-finish${tripReady ? " tp-spot-complete" : ""}`}>
                <span aria-hidden="true" className="tp-spot-ring">🏁</span>
                <span className="tp-spot-name">Trip-ready</span>
                <span className="tp-spot-meta">
                  {tripReady ? "Bon voyage!" : `${sections.length - stamped.length} to go`}
                </span>
              </div>
            </li>
          </ol>
        </section>

        {toolbar}

        <p className="tp-hint">
          10 phrases a round. Phrases you get right come back in a day, then three, then a
          week, so they stick. Saved in this browser, no account needed.
        </p>
      </div>
    );
  }

  if (phase === "done") {
    const right = results.filter((result) => result.right).length;
    const newStamps = stamped.filter((section) => !stampedBefore.includes(section));
    const continueTo = newStamps.length ? upNext : (pack === REVIEW_PACK ? null : pack);

    return (
      <div className="tp tp-done">
        <div aria-hidden="true" className="tp-burst">
          {celebration.pieces.map((piece) => (
            <span
              className="tp-burst-piece"
              key={piece.key}
              style={{
                "--drift": `${piece.drift}px`,
                "--fall": `${piece.fall}px`,
                "--rise": `${piece.rise}px`,
                "--spin": `${piece.spin}deg`,
                animationDelay: `${piece.delay}ms`,
                animationDuration: `${piece.duration}ms`,
                fontSize: `${piece.size}px`,
              }}
            >
              {newStamps.length ? stopIcon(newStamps[0]) : celebration.emoji}
            </span>
          ))}
        </div>

        {newStamps.length ? (
          <div className="tp-stamped">
            {newStamps.map((section) => (
              <div className="tp-stamp-big" key={section}>
                <span className="tp-stamp-big-icon">{stopIcon(section)}</span>
                <span className="tp-stamp-big-name">{section}</span>
                <span className="tp-stamp-big-word">Stamped</span>
              </div>
            ))}
            <h2 className="tp-done-headline">
              {tripReady ? "You're trip-ready!" : "Stop complete!"}
            </h2>
            <p className="tp-stamped-text">
              {tripReady
                ? `That's every phrase in the ${languageName} phrasebook.`
                : `You've got every phrase in ${newStamps.join(" and ")}. ${stamped.length} of ${sections.length} stamps.`}
            </p>
          </div>
        ) : (
          <>
            <div className="tp-done-score">{right}/{results.length}</div>
            <h2 className="tp-done-headline">{celebration.headline}</h2>
          </>
        )}

        <div className="tp-pass">
          <div className="tp-pass-main">
            <div className="tp-pass-label">Boarding pass · Clozemaster</div>
            <div className="tp-pass-route">
              <span>English</span>
              <span aria-hidden="true" className="tp-pass-plane">✈</span>
              <span>{languageName}</span>
            </div>
            <div className="tp-pass-fields">
              <div><small>Stamps</small>{stamped.length}/{sections.length}</div>
              <div><small>Phrases</small>{learnedCount}/{phrases.length}</div>
              <div><small>Next stop</small>{upNext || "Trip-ready"}</div>
            </div>
          </div>
          <div className="tp-pass-stub">
            <small>Gate</small>
            <span aria-hidden="true">{pack === REVIEW_PACK ? "🔁" : stopIcon(pack)}</span>
          </div>
        </div>

        <table className="tp-results">
          <tbody>
            {results.map(({ phrase, right: wasRight }) => (
              <tr key={phrase.id}>
                <td className="tp-results-mark">
                  <span aria-label={wasRight ? "Correct" : "Missed"} className={`glyphicon glyphicon-${wasRight ? "ok" : "remove"}`} />
                </td>
                <td>
                  <div className="tp-results-target" lang={speechLang || undefined}>
                    {phrase.target}
                    {said[phrase.id] && <span className="tp-results-said" title="Said out loud"> 🎙</span>}
                  </div>
                  <div className="tp-results-en">{phrase.en}</div>
                </td>
                <td className="tp-results-actions">
                  <PlayButton available={canPlay(phrase)} onPlay={play} phrase={phrase} />
                  <CaptureButton phrase={phrase} quickCapture={quickCapture} small />
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="tp-done-actions">
          {continueTo ? (
            <button className="btn btn-success btn-lg tp-primary" onClick={() => startRound(continueTo)} type="button">
              {newStamps.length ? `Next stop: ${continueTo}` : "Keep going"} →
            </button>
          ) : null}
          <button className="btn btn-default btn-lg" onClick={leaveRound} type="button">
            🗺 Back to the route
          </button>
        </div>

        {shareButton}
        {saveNudge}

      </div>
    );
  }

  const isRight = picked && picked.id === current.id;
  const targetLang = speechLang || undefined;
  const choicesAreTargets = kind === "produce";

  let prompt;
  if (isLearnStep) {
    prompt = (
      <div className="tp-learn">
        <div className="tp-ask"><span className="tp-new-badge">New phrase</span></div>
        <div className="tp-learn-en">
          {current.en}
          {current.note && <span className="tp-note"> ({current.note})</span>}
        </div>
        <div className="tp-prompt tp-prompt-target" lang={targetLang}>
          {current.target}
          <PlayButton available={canPlay(current)} onPlay={play} phrase={current} />
        </div>
        <Pronunciation phrase={current} />
      </div>
    );
  } else if (kind === "produce") {
    prompt = (
      <>
        <div className="tp-ask">How do you say…</div>
        <div className="tp-prompt">{current.en}</div>
        {current.note && <div className="tp-note">({current.note})</div>}
      </>
    );
  } else if (kind === "listen" && !picked) {
    prompt = (
      <>
        <div className="tp-ask">Listen. What does it mean?</div>
        <button aria-label="Play it again" className="tp-listen" onClick={() => play(current)} type="button">
          <span className="glyphicon glyphicon-volume-up" />
        </button>
      </>
    );
  } else {
    prompt = (
      <>
        <div className="tp-ask">{kind === "listen" ? "Listen. What does it mean?" : "What does this mean?"}</div>
        <div className="tp-prompt tp-prompt-target" lang={targetLang}>
          {current.target}
          <PlayButton available={canPlay(current)} onPlay={play} phrase={current} />
        </div>
        <Pronunciation phrase={current} />
      </>
    );
  }

  return (
    <div className="tp tp-playing">
      <div className="tp-card">
        <div className="tp-card-meta">
          <span className="tp-card-meta-left">
            <button className="tp-mini-tool tp-tool-on tp-back" onClick={leaveRound} title="Back to your passport" type="button">
              ← Passport
            </button>
            <span>{pack === REVIEW_PACK ? "🔁 Review" : `${stopIcon(pack)} ${pack}`}</span>
          </span>
          <span className="tp-card-meta-right">
            {/* Settings stay reachable mid-round. */}
            {hasAudio && (
              <button
                aria-label="Play audio automatically"
                aria-pressed={!!settings.autoplay}
                className={`tp-mini-tool${settings.autoplay ? " tp-tool-on" : ""}`}
                onClick={() => updateSettings({ autoplay: !settings.autoplay })}
                title={`Auto-play audio: ${settings.autoplay ? "on" : "off"}`}
                type="button"
              >
                {settings.autoplay ? "🔊" : "🔈"}
              </button>
            )}
            {(generatedAvailable || deviceVoices.length > 0) && (
              <button
                aria-expanded={showVoices}
                aria-label="Choose voice"
                className={`tp-mini-tool${showVoices ? " tp-tool-on" : ""}`}
                onClick={() => setShowVoices(!showVoices)}
                title="Choose voice"
                type="button"
              >
                🗣
              </button>
            )}
            <button
              aria-label={`Theme: ${THEME_LABELS[theme]}`}
              className="tp-mini-tool tp-tool-on"
              onClick={cycleTheme}
              title={theme === "auto" ? `Theme: auto (following your system, ${systemDark ? "dark" : "light"})` : `Theme: ${theme}`}
              type="button"
            >
              {themeIcon}
            </button>
            <button
              aria-label="Sound effects"
              aria-pressed={!!settings.sounds}
              className={`tp-mini-tool${settings.sounds ? " tp-tool-on" : ""}`}
              onClick={() => updateSettings({ sounds: !settings.sounds })}
              title={`Sound effects: ${settings.sounds ? "on" : "off"}`}
              type="button"
            >
              {settings.sounds ? "🔔" : "🔕"}
            </button>
            <span>{Math.min(position + 1, queue.length)} / {queue.length}</span>
          </span>
        </div>

        {showVoices && <div className="tp-voice-row">{voicePicker}</div>}

        {prompt}

        {isLearnStep ? (
          <>
            <div className="tp-reveal-actions">
              <button className="btn btn-success btn-lg tp-primary" onClick={next} type="button">
                Got it →
              </button>
              <CaptureButton phrase={current} quickCapture={quickCapture} />
            </div>
            <SayIt
              canPlayOriginal={canPlay(current)}
              key={`say-${current.id}-${position}`}
              onPlayOriginal={play}
              onRecorded={markSaid}
              phrase={current}
            />
          </>
        ) : (
          <div className="tp-choices">
            {choices.map((choice, index) => {
              let state = "";
              if (picked) {
                if (choice.id === current.id) state = " tp-choice-right";
                else if (choice.id === picked.id) state = " tp-choice-wrong";
                else state = " tp-choice-dim";
              }

              return (
                <button
                  className={`tp-choice${state}`}
                  disabled={!!picked}
                  key={choice.id}
                  onClick={() => answer(choice)}
                  type="button"
                >
                  <span className="tp-choice-key">{index + 1}</span>
                  <span className="tp-choice-text" lang={choicesAreTargets ? targetLang : undefined}>
                    {choicesAreTargets ? choice.target : choice.en}
                  </span>
                </button>
              );
            })}
          </div>
        )}

        {picked && (
          <div className={`tp-reveal${isRight ? " tp-reveal-right" : " tp-reveal-wrong"}`}>
            <div className="tp-reveal-verdict">{isRight ? "Correct!" : "Not quite."}</div>
            <div className="tp-reveal-target" lang={targetLang}>
              {current.target}
              <PlayButton available={canPlay(current)} onPlay={play} phrase={current} />
            </div>
            <Pronunciation phrase={current} />
            <div className="tp-reveal-en">
              {current.en}
              {current.note && <span className="tp-note"> ({current.note})</span>}
            </div>

            <div className="tp-reveal-actions">
              <button className="btn btn-success btn-lg tp-primary" onClick={next} type="button">
                Continue
              </button>
              <CaptureButton phrase={current} quickCapture={quickCapture} />
            </div>

            <SayIt
              canPlayOriginal={canPlay(current)}
              key={`say-${current.id}-${position}`}
              onPlayOriginal={play}
              onRecorded={markSaid}
              phrase={current}
            />
            <ReportPhrase key={current.id} phrase={current} reportUrl={reportUrl} />
          </div>
        )}
      </div>

      <div className="tp-hint">{isLearnStep ? "enter to continue" : "1–4 to answer · enter to continue"}</div>
    </div>
  );
}

TravelPhrases.defaultProps = {
  audioBaseUrl: null,
  correctAudioUrl: null,
  gameSettingsUrl: null,
  isSignedIn: false,
  quickCapture: null,
  signInUrl: null,
  signUpUrl: null,
  speechLang: null,
  stateUrl: null,
  takeaways: null,
};

TravelPhrases.propTypes = {
  audioBaseUrl: PropTypes.string,
  correctAudioUrl: PropTypes.string,
  gameSettingsUrl: PropTypes.string,
  isSignedIn: PropTypes.bool,
  languageName: PropTypes.string.isRequired,
  phrases: PropTypes.arrayOf(PropTypes.shape({
    audio: PropTypes.string,
    en: PropTypes.string.isRequired,
    id: PropTypes.string.isRequired,
    note: PropTypes.string,
    pron: PropTypes.string,
    romanization: PropTypes.string,
    section: PropTypes.string,
    speak: PropTypes.string,
    target: PropTypes.string.isRequired,
  })).isRequired,
  quickCapture: PropTypes.object,
  reportUrl: PropTypes.string.isRequired,
  shareUrl: PropTypes.string.isRequired,
  signInUrl: PropTypes.string,
  signUpUrl: PropTypes.string,
  slug: PropTypes.string.isRequired,
  speechLang: PropTypes.string,
  stateUrl: PropTypes.string,
  takeaways: PropTypes.shape({
    cheatSheetUrl: PropTypes.string.isRequired,
    wallpaper: PropTypes.object.isRequired,
  }),
};
